<template>
  <modal v-on:cancel="closeModal">
    <template v-slot:title>
      <p class="modal-card-title">Create Contact Attribute</p>
    </template>

    <template v-slot:body>
      <fieldset>
        <div class="field">
          <label class="label">ID</label>
          <ReadonlyField :readonlyValue="attribute.id" />
        </div>
        <div class="field">
          <label class="label" for="name">Name</label>
          <div class="control">
            <input
              ref="nameInput"
              type="text"
              class="input"
              id="name"
              autocomplete="off"
              v-model="attribute.name"
              @input="$v.attribute.name.$touch()"
            />
            <p class="help is-danger" v-if="isFieldNotPopulated($v.attribute, 'name')">Name is required</p>
            <p class="help is-danger" v-else-if="isFieldInvalid($v.attribute, 'name')">
              Name may only contain letters, numbers, spaces, underscores or hyphens, and must be no longer than 50
              characters
            </p>
          </div>
        </div>
        <div class="field">
          <label class="label" for="description">Description</label>
          <div class="control">
            <textarea
              class="textarea"
              id="description"
              v-model="attribute.description"
              @input="$v.attribute.description.$touch()"
            />
            <p class="help is-danger" v-if="isFieldNotPopulated($v.attribute, 'description')">
              Description is required
            </p>
            <p class="help is-danger" v-else-if="isFieldInvalid($v.attribute, 'description')">
              Description must be no longer than 255 characters
            </p>
          </div>
        </div>
        <div class="field">
          <label class="label" for="dataType">Data type</label>
          <div class="control select">
            <select id="dataType" v-model="attribute.type">
              <option v-for="type in dataTypeOptions" :key="type.value" :value="type.value">{{ type.display }}</option>
            </select>
          </div>
        </div>
      </fieldset>
    </template>

    <template v-slot:buttons>
      <n-button-group>
        <n-button
          id="create-attribute-submit-btn"
          variant="primary"
          :disabled="$v.$invalid"
          :loading="updateInProgress"
          @click="update"
        >
          Create Attribute
        </n-button>
        <n-button @click="closeModal">Cancel</n-button>
      </n-button-group>
    </template>
  </modal>
</template>

<script lang="ts" setup>
import { CreateContactAttributeViewModel } from '@/messaging/models/createContactAttributeViewModel';
import Toast from '@/shared/services/toast';
import { ContactAttributeType } from '@/messaging/models/contactAttributeType';
import ReadonlyField from '@/shared/components/ReadonlyField.vue';
import Modal from '@/shared/components/Modal.vue';
import { CREATE_CONTACT_ATTRIBUTE } from '@/store/attributes/actionTypes';
import { ContactAttributeViewModel } from '@/messaging/models/contactAttributeViewModel';
import { useStore } from 'vuex';
import { onMounted, PropType, ref, watch } from 'vue';
import useVuelidate from '@vuelidate/core';
import { maxLength, required } from '@vuelidate/validators';

const props = defineProps({
  excludedDataTypes: {
    type: Array as PropType<ContactAttributeType[]>,
    default: [],
  },
});

const store = useStore();
const createAttribute = (request: CreateContactAttributeViewModel): Promise<ContactAttributeViewModel> =>
  store.dispatch(CREATE_CONTACT_ATTRIBUTE, request);

const attribute = ref<CreateContactAttributeViewModel>({
  id: '',
  name: '',
  description: '',
  type: 'text',
});
const dataTypeOptions = ref([
  { value: ContactAttributeType.Text, display: 'Text' },
  { value: ContactAttributeType.Number, display: 'Number' },
  { value: ContactAttributeType.Decimal, display: 'Decimal' },
  { value: ContactAttributeType.Date, display: 'Date' },
]);
const updateInProgress = ref(false);
const nameInput = ref<HTMLInputElement | null>(null);

const emit = defineEmits<{
  (e: 'cancel'): void;
  (e: 'create', attribute: ContactAttributeViewModel): void;
}>();

const validName = (value: string) => value.search(/[^a-zA-Z0-9 _\-]/) === -1;
const validations = {
  attribute: {
    description: {
      required,
      maxLength: maxLength(255),
    },
    name: {
      required,
      validName,
      maxLength: maxLength(50),
    },
  },
};

const $v = useVuelidate(validations, { attribute });

watch(
  () => attribute.value.name,
  (to) => (attribute.value.id = toCamelCase(to)),
  { immediate: true },
);

onMounted(() => {
  if (nameInput.value) {
    nameInput.value.focus();
  }

  if (props.excludedDataTypes) {
    dataTypeOptions.value = dataTypeOptions.value.filter(
      (t) => !props.excludedDataTypes.find((excluded) => excluded === t.value),
    );
  }
});

function closeModal() {
  emit('cancel');
}

function isFieldNotPopulated(vuelidateObject: any, fieldName: string): boolean {
  const field = vuelidateObject[fieldName];
  return !field || (field.$invalid && !field.required && field.$dirty);
}

function isFieldInvalid(vuelidateObject: any, fieldName: string): boolean {
  const field = vuelidateObject[fieldName];
  return !field || (field.$invalid && field.$dirty);
}

async function update() {
  if (updateInProgress.value) {
    return;
  }

  updateInProgress.value = true;
  try {
    const createdAttribute = await createAttribute(attribute.value);
    Toast.showSuccess('Attribute successfully created');
    emit('create', createdAttribute);
  } catch (err) {
    Toast.showError(err.message);
  } finally {
    updateInProgress.value = false;
  }
}

function toCamelCase(input: string): string {
  const words = toWords(input);
  let result = '';
  for (let i = 0; i < words.length; i++) {
    let word = words[i].toLowerCase();
    if (i !== 0) {
      word = word.substr(0, 1).toUpperCase() + word.substr(1);
    }

    result += word;
  }
  return result;
}

function toWords(input: string): RegExpMatchArray {
  if (!input) {
    return [] as unknown as RegExpMatchArray;
  }

  const regex =
    /[A-Z\xC0-\xD6\xD8-\xDE]?[a-z\xDF-\xF6\xF8-\xFF]+|[A-Z\xC0-\xD6\xD8-\xDE]+(?![a-z\xDF-\xF6\xF8-\xFF])|\d+/g;
  return input.match(regex) as RegExpMatchArray;
}
</script>
